<template>
  <Teleport to="body">
    <div class="modal-overlay" @click.self="$emit('close')">
      <div class="modal-panel">
        <div class="modal-header">
          <h3 class="modal-title">Histórico: {{ itemName }}</h3>
          <button class="modal-close" type="button" @click="$emit('close')">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>

        <div v-if="loading" class="modal-loading">Carregando histórico...</div>
        <div v-else-if="movements.length === 0" class="modal-empty">
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          Nenhuma movimentação registrada.
        </div>
        <div v-else class="movements-list">
          <div v-for="m in movements" :key="m.id" class="movement-row">
            <div class="mov-type-badge" :class="movTypeClass(m.movementType)">
              <svg v-if="m.movementType === 'saida'" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              <svg v-else-if="m.movementType === 'entrada-nf'" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
              <svg v-else width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>
              {{ movTypeLabel(m.movementType) }}
            </div>
            <div class="mov-info">
              <div class="mov-qty" :class="m.movementType === 'saida' ? 'mov-qty--out' : 'mov-qty--in'">
                {{ m.movementType === 'saida' ? '-' : '+' }}{{ m.quantity }}
              </div>
              <div class="mov-meta">
                <span class="mov-date">{{ formatDate(m.movedAt) }}</span>
                <span v-if="m.movedBy" class="mov-user">· {{ m.movedBy }}</span>
              </div>
              <div v-if="m.notes" class="mov-notes">{{ m.notes }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script lang="ts">
import { defineComponent, ref, watch, onMounted } from 'vue'
import { apiFetch } from '@/store/api'

interface Movement {
  id: string; movementType: string; quantity: number
  notes: string; movedBy: string; movedAt: string
}

const MOV_TYPE_LABELS: Record<string, string> = { entrada: 'Entrada', saida: 'Saída', 'entrada-nf': 'Entrada NF' }
const MOV_TYPE_CLASS: Record<string, string> = { entrada: 'type--green', saida: 'type--red', 'entrada-nf': 'type--blue' }

export default defineComponent({
  name: 'WarehouseMovementHistoryModal',
  props: {
    itemId: { type: String, required: true },
    itemName: { type: String, default: '' }
  },
  emits: ['close'],
  setup(props) {
    const movements = ref<Movement[]>([])
    const loading = ref(true)

    const load = async () => {
      loading.value = true
      try {
        movements.value = await apiFetch<Movement[]>(`/warehouse/movements?itemId=${encodeURIComponent(props.itemId)}`)
      } catch { movements.value = [] }
      finally { loading.value = false }
    }

    const formatDate = (d: string) => {
      const m = `${d || ''}`.match(/(\d{4})-(\d{2})-(\d{2})/)
      return m ? `${m[3]}/${m[2]}/${m[1]}` : d
    }
    const movTypeLabel = (t: string) => MOV_TYPE_LABELS[t] || t
    const movTypeClass = (t: string) => MOV_TYPE_CLASS[t] || ''

    watch(() => props.itemId, load)
    onMounted(load)

    return { movements, loading, formatDate, movTypeLabel, movTypeClass }
  }
})
</script>

<style scoped lang="scss">
.modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,.45); z-index: 2000;
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.modal-panel {
  background: #fff; border-radius: 16px; width: 100%; max-width: 520px;
  max-height: 80vh; display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,.18);
}
.modal-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 18px 20px 14px; border-bottom: 1px solid rgba(31,30,26,.1); flex-shrink: 0;
}
.modal-title { margin: 0; font-size: 16px; font-weight: 800; color: var(--ink, #2c2b27); }
.modal-close {
  background: none; border: none; cursor: pointer; color: var(--ink-muted, #888); padding: 4px;
  border-radius: 6px; display: flex; align-items: center; justify-content: center;
  &:hover { background: rgba(31,30,26,.06); }
}
.modal-loading { padding: 40px; text-align: center; font-size: 13px; color: var(--ink-muted, #888); }
.modal-empty {
  display: flex; flex-direction: column; align-items: center; gap: 10px;
  padding: 50px 20px; color: var(--ink-muted, #aaa); text-align: center; font-size: 14px;
  svg { opacity: .35; }
}
.movements-list { overflow-y: auto; flex: 1; padding: 12px 16px; display: flex; flex-direction: column; gap: 8px; }
.movement-row {
  display: flex; align-items: flex-start; gap: 10px; padding: 10px 12px;
  border: 1px solid rgba(31,30,26,.08); border-radius: 8px; background: #fafaf9;
}
.mov-type-badge {
  display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0;
  font-size: 10px; font-weight: 700; padding: 3px 8px; border-radius: 20px;
  &.type--green { background: #dcfce7; color: #15803d; }
  &.type--red { background: #fef2f2; color: #dc2626; }
  &.type--blue { background: #dbeafe; color: #1d4ed8; }
}
.mov-info { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.mov-qty { font-size: 16px; font-weight: 800; &--out { color: #dc2626; } &--in { color: #15803d; } }
.mov-meta { display: flex; gap: 6px; align-items: center; }
.mov-date { font-size: 11px; color: var(--ink-muted, #888); }
.mov-user { font-size: 11px; color: var(--ink-muted, #888); }
.mov-notes { font-size: 12px; color: var(--ink-muted, #777); font-style: italic; }
</style>
